import React, { useEffect, useState } from 'react'
import { IProduct, ProductInfo } from '../../interfaces/product';
import useConfirmationModal from '../../hooks/useConfirmationModal';
import { callApi } from '../../api/callApi';
import { API_ENDPOINT, API_URL_2 } from '../../api/constant';
import InputField from '../../components/UI/Forms/InputField';
import ErrorMessage from '../../components/UI/Notifications/ErrorMessage';
import Layout from '../../components/Layout/Layout';
import Table from '../../components/UI/CommonTable/CommonTable';
import ConfirmationModal from '../../components/UI/Modal/ConfirmationModal';
import Button from '../../components/UI/Buttons/Button';
import { useForm } from 'react-hook-form';
import Pagination from '../../components/UI/List/Pagination';
import moment from '../../utility/moment-timezone';
import { toFixedThree } from '../../utility/helper';

interface IFormInputs {
    selectedProducts: string[]; // IDs of selected products for discount addition
    discountPercentage: number;
    discountStartDate: string;
    discountEndDate: string;
}

const DiscountProducts: React.FC = () => {
    const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<IFormInputs>({ mode: 'onBlur' });
    const startDate = watch('discountStartDate');
    const today = new Date().toISOString().split('T')[0]
    const [discountProducts, setDiscountProducts] = useState<IProduct[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [products, setProducts] = useState<{ value: string; label: string }[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const { showModal, modalProps, confirmAction } = useConfirmationModal();
    const [loadingButton, setLoadingButton] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedDiscountProducts, setSelectedDiscountProducts] = useState<string[]>([]);

    // Fetch discount products list for the current page
    useEffect(() => {
        getProducts(currentPage);
    }, [currentPage]);

    useEffect(() => {
        setValue('selectedProducts', selectedDiscountProducts, { shouldValidate: true });
    }, [selectedDiscountProducts, setValue]);

    const getProducts = async (page: number = 1) => {
        try {
            setLoading(true);
            const response = await callApi(API_URL_2 + API_ENDPOINT.page + `products?page=${page}&discount=true`, { method: 'GET' });
            const { currentPage, data, totalPages } = response;
            setDiscountProducts(data);
            setCurrentPage(currentPage);
            setTotalPages(totalPages);
        } catch (error: any) {
            console.error('Failed to fetch products:', error.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };



    // Fetch product suggestions as the user types in the search field
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchQuery) {
                fetchProducts(searchQuery)
            } else {
                setProducts([])
            }
        }, 300); // 300ms debounce

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery]);

    const fetchProducts = async (query: string) => {
        try {
            const response = await callApi(API_URL_2 + API_ENDPOINT.page + `products?title=${query}`, { method: 'GET' });
            // Map the response to match the multi-select format (value and label)
            const newProducts = response.data.map((prod: any) => ({ value: prod.id, label: prod.title }));
            setProducts(newProducts);
        } catch (error: any) {
            console.error('Failed to fetch products:', error.message);
        }
    };

    // Called when the user submits the “Add Discount” form
    const onSubmit = async (data: IFormInputs) => {
        try {
            setLoadingButton(true);
            // API payload for adding discount products
            const payload = {
                id: data.selectedProducts,
                discountPercentage: data.discountPercentage,
                discountStartDate: data.discountStartDate,
                discountEndDate: data.discountEndDate,
                type: 'add'
            };
            await callApi(API_URL_2 + API_ENDPOINT.page + `products/discount`, { method: 'PATCH', data: payload });
            // Optionally refresh discount products list;
            setProducts([])
            setSelectedDiscountProducts([])
            getProducts(currentPage);
            reset(); // reset the form
        } catch (error: any) {
            console.error('Failed to add discount products:', error.message);
        } finally {
            setLoadingButton(false);
        }
    };

    // For individual product removal (removing discount)
    const deleteProduct = async (ids: string[]) => {
        try {
            const payload = {
                id: ids,
                discountPercentage: 0,
                type: 'remove'
            };
            await callApi(API_URL_2 + API_ENDPOINT.page + `products/discount`, { method: 'PATCH', data: payload });
            // Refresh list after removal
            getProducts(currentPage);
        } catch (error: any) {
            console.error('Failed to remove discount from product:', error.message);
        }
    };

    // Confirm and call deleteProduct on individual deletion
    const handleDeleteClick = (ids: string[]) => {
        confirmAction({
            title: 'Confirm Remove Discount',
            message: 'Are you sure you want to remove the discount from the selected products?',
            onConfirm: () => deleteProduct(ids),
        });
    };

    // Bulk removal for selected discount products from the table
    const handleBulkRemove = async () => {
        if (selectedDiscountProducts.length === 0) return;
        handleDeleteClick(selectedDiscountProducts)
    };

    // Table columns definition
    const columns = [
        {
            header: "Select",
            accessor: "id",
            render: (id: string) => (
                <input
                    type="checkbox"
                    checked={selectedDiscountProducts.includes(id)}
                    className='form-check-input'
                    onChange={(e) => {
                        if (e.target.checked) {
                            console.log("checked", selectedDiscountProducts)
                            setSelectedDiscountProducts([...selectedDiscountProducts, id]);
                        } else {
                            setSelectedDiscountProducts(selectedDiscountProducts.filter(productId => productId !== id));
                        }
                    }}
                />
            )
        },
        {
            header: "Image",
            accessor: "images",
            render: (images: string[]) => (
                <div className='table-image'>
                    <img className="img-image" src={images && images[0]} alt="product" />
                </div>
            ),
        },
        { header: "Title", accessor: "title" },
        {
            header: "Price",
            accessor: "productInfo",
            render: (productInfo: ProductInfo[]) => toFixedThree(productInfo && productInfo[0]?.price)
        },
        {
            header: 'Discount %',
            accessor: 'productInfo',
            render: (productInfo: ProductInfo[]) => productInfo && productInfo[0]?.discountPercentage
        },
        {
            header: "Start Date",
            accessor: "productInfo",
            render: (productInfo: ProductInfo[]) => moment(productInfo && productInfo[0]?.discountStartDate).format("DD MMM, YYYY")
        },
        {
            header: "End Date",
            accessor: "productInfo",
            render: (productInfo: ProductInfo[]) => moment(productInfo && productInfo[0]?.discountEndDate).format("DD MMM, YYYY")
        },
        {
            header: "Type Of Meat",
            accessor: "typeOfMeat",
            render: (types: string[]) => types.join(", "),
        },
    ];

    // Action for each row – individual removal
    const actions = [
        {
            icon: "bi bi-trash3",
            color: "danger",
            onClick: (product: IProduct) => handleDeleteClick([product.id]),
        },
    ];

    const toggleSelection = (productId: string) => {
        setSelectedDiscountProducts(prevSelected =>
            prevSelected.includes(productId)
                ? prevSelected.filter(id => id !== productId)
                : [...prevSelected, productId]
        );
    };

    return (
        <Layout>
            <div className='d-flex justify-content-between align-items-start'>
                <h1 className="app-page-title">Discount Products</h1>
                {selectedDiscountProducts.length > 0 && <button onClick={() => handleBulkRemove()} className='btn app-btn-primary d-flex align-items-center gap-1'><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M6 0C6.13261 0 6.25979 0.0526785 6.35355 0.146447C6.44732 0.240215 6.5 0.367392 6.5 0.5V5.5H11.5C11.6326 5.5 11.7598 5.55268 11.8536 5.64645C11.9473 5.74021 12 5.86739 12 6C12 6.13261 11.9473 6.25979 11.8536 6.35355C11.7598 6.44732 11.6326 6.5 11.5 6.5H6.5V11.5C6.5 11.6326 6.44732 11.7598 6.35355 11.8536C6.25979 11.9473 6.13261 12 6 12C5.86739 12 5.74021 11.9473 5.64645 11.8536C5.55268 11.7598 5.5 11.6326 5.5 11.5V6.5H0.5C0.367392 6.5 0.240215 6.44732 0.146447 6.35355C0.0526785 6.25979 0 6.13261 0 6C0 5.86739 0.0526785 5.74021 0.146447 5.64645C0.240215 5.55268 0.367392 5.5 0.5 5.5H5.5V0.5C5.5 0.367392 5.55268 0.240215 5.64645 0.146447C5.74021 0.0526785 5.86739 0 6 0Z" fill="white" />
                </svg> Remove Selected Discount</button>}
            </div>
            <div className="row mb-4">
                {/* Table and Bulk Remove Section */}
                <div className="col-lg-9">
                    <div className="app-card app-card-orders-table shadow-sm mb-4 p-4">
                        <div className="app-card-body">
                            <Table
                                columns={columns}
                                data={discountProducts}
                                loading={loading}
                                actions={actions}
                                emptyMessage="No discounted products available."
                            />
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-lg-3">
                    <div className="app-card app-card-settings shadow-sm p-4">
                        <div className="app-card-body">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                {/* Product Search & Multi-Select */}
                                <div className="mb-3">
                                    <label htmlFor="productSearch" className="form-label">
                                        Search Product
                                    </label>
                                    <input
                                        id="productSearch"
                                        type="text"
                                        className="form-control"
                                        placeholder="Type product title..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>

                                {/* Products List */}
                                {products.length > 0 && (
                                    <div className="mb-3">
                                        <label className="form-label">Select Products</label>
                                        <div className="list-group">
                                            {products.map((product) => {
                                                const isSelected = selectedDiscountProducts.includes(product.value);
                                                return (
                                                    <button
                                                        type="button"
                                                        key={product.value}
                                                        onClick={() => toggleSelection(product.value)}
                                                        className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${isSelected ? 'active' : ''
                                                            }`}
                                                    >
                                                        <span>{product.label}</span>
                                                        {isSelected && <span className="badge bg-light text-success">&#10003;</span>}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {/* Display an error message if no product is selected */}
                                        {errors.selectedProducts && (
                                            <p className="text-danger mt-2">{errors.selectedProducts.message}</p>
                                        )}
                                    </div>
                                )}
                                {/* Discount Percentage Input */}
                                <div className="mb-3">
                                    <InputField
                                        type="number"
                                        register={register('discountPercentage', {
                                            required: 'Discount percentage is required',
                                            min: { value: 1, message: 'Discount percentage must be greater than 0' },
                                            max: { value: 100, message: 'Discount percentage must be 100 or less' }
                                        })}
                                        name="discountPercentage"
                                        label="Discount Percentage"
                                        placeholder="Enter discount percentage"
                                    />
                                    <ErrorMessage message={errors.discountPercentage?.message?.toString()} />
                                </div>

                                <div className="mb-3">
                                    <InputField
                                        type="date"
                                        register={register('discountStartDate', {
                                            required: 'Discount Start Date is required',
                                            validate: (value) =>
                                                value >= today
                                                    ? true
                                                    : `Start date cannot be in the past (earliest is ${today})`,
                                        })}
                                        name="discountStartDate"
                                        label="Start Date"
                                        placeholder="Enter Start Date"
                                    />
                                    <ErrorMessage message={errors.discountStartDate?.message?.toString()} />
                                </div>
                                <div className="mb-3">
                                    <InputField
                                        type="date"
                                        register={register('discountEndDate', {
                                            required: 'Discount End Date is required',
                                            validate: (value) =>
                                                !startDate || value >= startDate
                                                    ? true
                                                    : 'End date must be the same or after start date',
                                        })}
                                        name="discountEndDate"
                                        label="End Date"
                                        placeholder="Enter End Date"
                                    />
                                    <ErrorMessage message={errors.discountEndDate?.message?.toString()} />
                                </div>
                                <div className="text-end mt-3">
                                    <Button loading={loadingButton} className="btn btn-primary text-white px-5" type="submit" text={'Add Discount'} />
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmationModal {...modalProps} show={showModal} />
        </Layout>
    )
}

export default DiscountProducts;
